// ===== PIXEL ASCENT MODERN - TYPE DEFINITIONS =====
// AI-First Architecture: All interfaces in one place for complete AI visibility

// ===== CORE GAME TYPES (Fixed Foundation) =====

export interface Vector2 {
  x: number;
  y: number;
}

export interface PlayerState {
  x: number;
  y: number;
  vx: number;                    // velocity x
  vy: number;                    // velocity y
  width: number;
  height: number;
  isGrounded: boolean;
  isAlive: boolean;
  color: string;
}

export interface Platform {
  x: number;
  y: number;
  width: number;
  height: number;
  type: 'normal' | 'dangerous' | 'gold' | 'moving' | 'bounce';
  color: string;
}

export interface GameConfig {
  canvas: {
    width: number;
    height: number;
  };
  physics: {
    gravity: number;           // pixels/second²
    jumpForce: number;         // pixels/second
    groundFriction: number;    // velocity multiplier
    airFriction: number;       // velocity multiplier
    maxVelocityX: number;      // max horizontal speed
    maxVelocityY: number;      // max vertical speed
  };
  lava: {
    riseSpeed: number;         // pixels/second
    startHeight: number;       // initial lava height
  };
  input: {
    keyRepeatDelay: number;    // milliseconds
    touchSensitivity: number;  // touch movement multiplier
  };
}

// ===== AI EXTENSION POINT TYPES =====

/* ========================================
 * AI EXTENSION POINT: COMBO SYSTEM
 * ========================================
 * To add combo functionality, uncomment and extend:
 */
export interface ComboState {
  count: number;
  multiplier: number;
  timeRemaining: number;
  isActive: boolean;
}

/* ========================================
 * AI EXTENSION POINT: POWER-UPS
 * ========================================
 * To add power-ups, uncomment and extend:
 */
export interface PowerUp {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: 'speed' | 'jump' | 'shield' | 'magnet';
  duration: number;          // milliseconds
  isActive: boolean;
  color: string;
}

/* ========================================
 * AI EXTENSION POINT: VISUAL EFFECTS
 * ========================================
 * To add effects, uncomment and extend:
 */
export interface Effect {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;              // 0-1, 1 = full life
  maxLife: number;           // milliseconds
  type: 'particle' | 'explosion' | 'trail';
  color: string;
  size: number;
}

/* ========================================
 * AI EXTENSION POINT: SOUND SYSTEM
 * ========================================
 * To add audio, uncomment and extend:
 */
export interface SoundEffect {
  id: string;
  src: string;
  volume: number;            // 0-1
  loop: boolean;
  isPlaying: boolean;
}

// ===== GAME STATE (Complete State in One Interface) =====

export interface GameState {
  // ===== CORE GAME STATE (Fixed Foundation) =====
  player: PlayerState;
  platforms: Platform[];
  lavaHeight: number;
  score: number;
  gameTime: number;          // elapsed milliseconds
  isGameOver: boolean;
  isPaused: boolean;
  
  // ===== INPUT STATE =====
  input: {
    keys: Record<string, boolean>;
    mouse: {
      x: number;
      y: number;
      isDown: boolean;
    };
    touch: {
      x: number;
      y: number;
      isActive: boolean;
    };
  };
  
  // ===== RENDERING STATE =====
  canvas: {
    width: number;
    height: number;
    context: CanvasRenderingContext2D | null;
  };
  
  // ===== AI EXTENSION POINTS (Where AI Adds Features) =====
  
  /* AI EXTENSION POINT: Add combo system here */
  combo: ComboState | null;
  
  /* AI EXTENSION POINT: Add power-ups here */
  powerUps: PowerUp[];
  
  /* AI EXTENSION POINT: Add visual effects here */
  effects: Effect[];
  
  /* AI EXTENSION POINT: Add sound system here */
  sounds: Record<string, SoundEffect>;
  
  /* AI EXTENSION POINT: Add level system here */
  currentLevel: number;
  
  /* AI EXTENSION POINT: Add achievements here */
  achievements: string[];
  
  // ===== GAME ACTIONS (All State Mutations) =====
  actions: {
    // Core game actions (fixed foundation)
    updatePlayer: (deltaTime: number) => void;
    updateLava: (deltaTime: number) => void;
    updateScore: () => void;
    checkCollisions: () => void;
    handleInput: () => void;
    resetGame: () => void;
    
    // AI EXTENSION POINT: Add combo actions here
    updateCombo?: (successful: boolean) => void;
    
    // AI EXTENSION POINT: Add power-up actions here
    spawnPowerUp?: (type: string, x: number, y: number) => void;
    collectPowerUp?: (powerUpId: string) => void;
    
    // AI EXTENSION POINT: Add effect actions here
    addEffect?: (effect: Omit<Effect, 'id'>) => void;
    updateEffects?: (deltaTime: number) => void;
    
    // AI EXTENSION POINT: Add sound actions here
    playSound?: (soundId: string) => void;
    stopSound?: (soundId: string) => void;
    
    // AI EXTENSION POINT: Add new actions here
  };
}

// ===== TEST TYPES (For AI Validation) =====

export interface TestResult {
  name: string;
  passed: boolean;
  error?: string;
  duration?: number;
}

export interface GameTestFramework {
  runAllTests: () => Promise<TestResult[]>;
  runPhysicsTests: () => Promise<TestResult[]>;
  runRenderingTests: () => Promise<TestResult[]>;
  runGameplayTests: () => Promise<TestResult[]>;
  addCustomTest: (name: string, testFn: () => Promise<boolean>) => void;
}

// ===== EVENT SYSTEM TYPES =====

export const GAME_EVENTS = {
  PLAYER_JUMP: 'player_jump',
  PLAYER_LAND: 'player_land',
  PLAYER_DEATH: 'player_death',
  SCORE_UPDATE: 'score_update',
  COMBO_START: 'combo_start',
  COMBO_END: 'combo_end',
  POWERUP_SPAWN: 'powerup_spawn',
  POWERUP_COLLECT: 'powerup_collect',
  LEVEL_COMPLETE: 'level_complete',
  GAME_OVER: 'game_over',
  GAME_RESTART: 'game_restart',
  // AI EXTENSION POINT: Add new events here
} as const;

export type GameEvent = typeof GAME_EVENTS[keyof typeof GAME_EVENTS];

export interface EventCallback {
  (data?: any): void;
}

export interface EventSystem {
  on: (event: GameEvent, callback: EventCallback) => void;
  off: (event: GameEvent, callback: EventCallback) => void;
  emit: (event: GameEvent, data?: any) => void;
}

// ===== PHYSICS CONSTANTS (Exposed for AI modification) =====

export const PHYSICS_CONSTANTS = {
  GRAVITY: 980,              // pixels/second²
  JUMP_FORCE: -400,          // pixels/second (negative = up)
  GROUND_FRICTION: 0.8,      // velocity multiplier when on ground
  AIR_FRICTION: 0.98,        // velocity multiplier when in air
  MAX_VELOCITY_X: 300,       // max horizontal speed
  MAX_VELOCITY_Y: 800,       // max vertical speed (falling)
  
  // AI EXTENSION POINT: Add new physics constants here
} as const;

// ===== GAME CONSTANTS (Exposed for AI modification) =====

export const GAME_CONSTANTS = {
  PLAYER_SIZE: { width: 20, height: 20 },
  PLATFORM_HEIGHT: 10,
  LAVA_RISE_SPEED: 2,        // pixels/second
  CANVAS_WIDTH: 800,
  CANVAS_HEIGHT: 600,
  
  // Colors
  COLORS: {
    PLAYER: '#00ff00',       // bright green
    PLATFORM: '#ffffff',     // white
    LAVA: '#ff4400',         // orange-red
    BACKGROUND: '#000000',   // black
    UI_TEXT: '#ffffff',      // white
    
    // AI EXTENSION POINT: Add new colors here
  },
  
  // AI EXTENSION POINT: Add new constants here
} as const;